import React, { useState, useEffect } from 'react';
import ProjectList from './pages/ProjectList';
import ProjectDetail from './pages/ProjectDetail';
import ScheduleLogPage from './pages/ScheduleLogPage';
import LoginPage from './pages/LoginPage';
import UserManagementPage from './pages/UserManagementPage';
import AuditLogPage from './pages/AuditLogPage';
import WorkloadPage from './pages/WorkloadPage';
import ARPage from './pages/ARPage';
import JobCostDashboardPage from './pages/JobCostDashboardPage';
import './App.css';

function App() {
  const [currentView, setCurrentView] = useState('projects');
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [projectTab, setProjectTab] = useState('dashboard');
  const [darkMode, setDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode ? JSON.parse(savedMode) : false;
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  // Auth state
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('e3_user');
    const token = localStorage.getItem('e3_auth_token');
    if (saved && token) {
      try { return JSON.parse(saved); } catch { return null; }
    }
    return null;
  });

  // Listen for auth expiry from API layer
  useEffect(() => {
    const handleAuthExpired = () => { setUser(null); };
    window.addEventListener('auth-expired', handleAuthExpired);
    return () => window.removeEventListener('auth-expired', handleAuthExpired);
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    setCurrentView('projects');
  };

  const handleLogout = () => {
    localStorage.removeItem('e3_auth_token');
    localStorage.removeItem('e3_user');
    setUser(null);
    setCurrentView('projects');
    setSelectedProjectId(null);
  };

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('darkMode', JSON.stringify(newMode));
  };

  const toggleSidebar = () => {
    const newVal = !sidebarCollapsed;
    setSidebarCollapsed(newVal);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newVal));
  };

  const openProject = (id) => {
    setSelectedProjectId(id);
    setProjectTab('dashboard');
    setCurrentView('project-detail');
  };

  const goBack = () => {
    setCurrentView('projects');
    setSelectedProjectId(null);
  };

  const openScheduleLog = () => {
    setCurrentView('schedule-log');
  };

  const openUserManagement = () => {
    setCurrentView('user-management');
  };

  const openAuditLog = () => {
    setCurrentView('audit-log');
  };

  const openWorkload = () => {
    setCurrentView('workload');
  };

  const openAR = () => {
    setCurrentView('ar');
  };

  const openJobCosts = () => {
    setCurrentView('job-costs');
  };

  // If not logged in, show login page
  if (!user) {
    return <LoginPage onLogin={handleLogin} />;
  }

  // Role hierarchy: admin > vp > construction_director > construction_manager > pm
  const roleRank = { admin: 5, vp: 4, construction_director: 3, construction_manager: 2, pm: 1 };
  const userRank = roleRank[user.role] || 0;
  const canManageUsers = userRank >= roleRank.vp;        // VP and Admin
  const canSeeScheduleLog = userRank >= roleRank.construction_manager; // CM and above

  // Sidebar nav items
  const sidebarNav = [
    { key: 'projects', label: 'Dashboard', abbr: 'D' },
    ...(canSeeScheduleLog ? [{ key: 'workload', label: 'Workload', abbr: 'W' }] : []),
    ...(canSeeScheduleLog ? [{ key: 'ar', label: 'AR / Collections', abbr: 'R' }] : []),
    ...(canSeeScheduleLog ? [{ key: 'job-costs', label: 'Job Costs', abbr: 'J' }] : []),
    ...(canSeeScheduleLog ? [{ key: 'schedule-log', label: 'Schedule Log', abbr: 'S' }] : []),
    ...(canSeeScheduleLog ? [{ key: 'audit-log', label: 'Audit Log', abbr: 'A' }] : []),
    ...(canManageUsers ? [{ key: 'user-management', label: 'Team', abbr: 'T' }] : []),
  ];

  // When inside a project, show project-level nav
  const projectNav = [
    { key: 'dashboard', label: 'Overview', abbr: 'O' },
    { key: 'sov', label: 'SOV', abbr: 'S' },
    { key: 'tasks', label: 'Tasks', abbr: 'T' },
    { key: 'rollup', label: 'Rollup', abbr: 'R' },
    { key: 'gantt', label: 'Gantt', abbr: 'G' },
    { key: 'billing', label: 'Billing', abbr: 'B' },
    { key: 'performance', label: 'Performance', abbr: 'P' },
    { key: 'forecast', label: 'Forecast', abbr: 'F' },
    { key: 'contract value', label: 'Contract Value', abbr: 'C' },
    { key: 'job-costs', label: 'Job Costs', abbr: 'J' },
    { key: 'client view', label: 'Client View', abbr: 'V' },
  ];

  return (
    <div className={`app ${darkMode ? 'dark' : ''} ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Sidebar */}
      <nav className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          {!sidebarCollapsed && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <img src="/logo.png" alt="E3 Summit" style={{ height: 28, width: 28, borderRadius: 6, objectFit: 'contain', flexShrink: 0 }} />
                <div style={{ lineHeight: 1 }}>
                  <span className="sidebar-app-name" onClick={goBack} style={{ cursor: 'pointer' }}>
                    E3 Summit
                  </span>
                  <span className="sidebar-app-subtitle">Project Tracking</span>
                </div>
              </div>
            </>
          )}
          <button
            className="sidebar-collapse-btn"
            onClick={toggleSidebar}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? '»' : '«'}
          </button>
        </div>
        <ul className="sidebar-nav">
          {currentView === 'projects' || currentView === 'schedule-log' || currentView === 'user-management' || currentView === 'audit-log' || currentView === 'workload' || currentView === 'ar' || currentView === 'job-costs' ? (
            sidebarNav.map((item) => (
              <li key={item.key} className="sidebar-nav-item">
                <span
                  className={`sidebar-nav-link ${currentView === item.key ? 'active' : ''}`}
                  onClick={() => {
                    if (item.key === 'projects') { goBack(); }
                    else if (item.key === 'workload') { openWorkload(); }
                    else if (item.key === 'ar') { openAR(); }
                    else if (item.key === 'job-costs') { openJobCosts(); }
                    else if (item.key === 'schedule-log') { openScheduleLog(); }
                    else if (item.key === 'audit-log') { openAuditLog(); }
                    else if (item.key === 'user-management') { openUserManagement(); }
                  }}
                  style={{ cursor: 'pointer' }}
                  title={sidebarCollapsed ? item.label : undefined}
                >
                  {sidebarCollapsed ? <span className="sidebar-nav-abbr">{item.abbr}</span> : item.label}
                </span>
              </li>
            ))
          ) : (
            <>
              <li className="sidebar-nav-item">
                <span
                  className="sidebar-nav-link"
                  onClick={goBack}
                  style={{ cursor: 'pointer' }}
                  title={sidebarCollapsed ? 'All Projects' : undefined}
                >
                  {sidebarCollapsed ? <span className="sidebar-nav-abbr">&larr;</span> : <><span style={{ marginRight: 6 }}>&larr;</span>All Projects</>}
                </span>
              </li>
              <li style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', margin: '8px 0' }} />
              {projectNav.map((item) => (
                <li key={item.key} className="sidebar-nav-item">
                  <span
                    className={`sidebar-nav-link ${projectTab === item.key ? 'active' : ''}`}
                    onClick={() => setProjectTab(item.key)}
                    style={{ cursor: 'pointer', fontSize: '12px' }}
                    title={sidebarCollapsed ? item.label : undefined}
                  >
                    {sidebarCollapsed ? <span className="sidebar-nav-abbr" style={{ fontSize: 11 }}>{item.abbr}</span> : item.label}
                  </span>
                </li>
              ))}
            </>
          )}
        </ul>

        {/* User section at bottom */}
        <div className="sidebar-footer">
          {sidebarCollapsed ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <div
                style={{
                  width: 30, height: 30, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700, color: '#fff',
                }}
                title={`${user.first_name} ${user.last_name}`}
              >
                {user.first_name[0]}{user.last_name[0]}
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button onClick={toggleDarkMode} title={`${darkMode ? 'Light' : 'Dark'} mode`} className="sidebar-footer-btn">
                  {darkMode ? '☀' : '☾'}
                </button>
                <button onClick={handleLogout} title="Sign out" className="sidebar-footer-btn">
                  ↪
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div
                style={{
                  width: 30, height: 30, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0,
                }}
                title={`${user.first_name} ${user.last_name} — ${{ admin:'Admin', vp:'Vice President', construction_director:'Const. Director', construction_manager:'Const. Manager', pm:'Project Manager' }[user.role] || user.role}`}
              >
                {user.first_name[0]}{user.last_name[0]}
              </div>
              <div style={{ display: 'flex', gap: 2 }}>
                <button onClick={toggleDarkMode} title={`${darkMode ? 'Light' : 'Dark'} mode`} className="sidebar-footer-btn">
                  {darkMode ? '☀' : '☾'}
                </button>
                <button onClick={handleLogout} title="Sign out" className="sidebar-footer-btn">
                  ↪
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <div className="main-content">
        <header className="app-header">
          <h1>
            {currentView === 'projects' ? 'Projects Dashboard'
              : currentView === 'workload' ? 'Team Workload'
              : currentView === 'ar' ? 'Accounts Receivable'
              : currentView === 'job-costs' ? 'Job Costs'
              : currentView === 'schedule-log' ? 'Schedule Change Log'
              : currentView === 'user-management' ? 'Team Management'
              : currentView === 'audit-log' ? 'Audit Log'
              : 'Project Detail'}
          </h1>
        </header>
        <div className="app-main">
          {currentView === 'projects' && (
            <ProjectList onSelectProject={openProject} />
          )}
          {currentView === 'project-detail' && selectedProjectId && (
            <ProjectDetail projectId={selectedProjectId} onBack={goBack} activeTab={projectTab} setActiveTab={setProjectTab} />
          )}
          {currentView === 'workload' && canSeeScheduleLog && (
            <WorkloadPage onOpenProject={openProject} currentUser={user} />
          )}
          {currentView === 'ar' && canSeeScheduleLog && (
            <ARPage onOpenProject={openProject} currentUser={user} />
          )}
          {currentView === 'job-costs' && canSeeScheduleLog && (
            <JobCostDashboardPage onOpenProject={openProject} currentUser={user} />
          )}
          {currentView === 'schedule-log' && canSeeScheduleLog && (
            <ScheduleLogPage onBack={goBack} onOpenProject={openProject} currentUser={user} />
          )}
          {currentView === 'user-management' && canManageUsers && (
            <UserManagementPage currentUser={user} />
          )}
          {currentView === 'audit-log' && canSeeScheduleLog && (
            <AuditLogPage onOpenProject={openProject} />
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
